import * as React from 'react'
import { cva, type VariantProps } from 'class-variance-authority'
import { cn } from 'lib/utils'

const buttonVariants = cva(
	'inline-flex items-center justify-center whitespace-nowrap text-xs rounded-full border-0 cursor-pointer transition-transform duration-150 disabled:opacity-50 disabled:pointer-events-none active:scale-[0.98] hover:scale-105 hover:no-underline',
	{
		variants: {
			variant: {
				primary: 'bg-primary text-primary-foreground hover:bg-[#0073e6]',
				success: 'bg-success text-success-foreground hover:bg-[#2f8559]',
				text: 'bg-white text-primary hover:bg-primary hover:text-white',
				danger: 'bg-destructive text-destructive-foreground hover:bg-[#c53030]',
				ghost: 'bg-muted text-primary hover:bg-[#e2e8f0]',
				'ghost-red': 'bg-transparent text-destructive hover:bg-destructive hover:text-white',
			},
			size: {
				default: 'px-2.5 py-0.5 text-xs',
				lg: 'px-5 py-3 text-xl',
				input: 'px-2 py-1',
			},
			isOutline: {
				true: 'bg-transparent',
				false: '',
			},
		},
		compoundVariants: [
			{
				variant: 'primary',
				isOutline: true,
				className:
					'bg-transparent text-primary ring-[1.5px] ring-inset ring-primary hover:bg-primary hover:text-white',
			},
			{
				variant: 'success',
				isOutline: true,
				className:
					'bg-transparent text-success ring-[1.5px] ring-inset ring-success hover:bg-success hover:text-white',
			},
			{
				variant: 'danger',
				isOutline: true,
				className:
					'bg-transparent text-destructive ring-[1.5px] ring-inset ring-destructive hover:bg-destructive hover:text-white',
			},
			{
				variant: 'ghost',
				isOutline: true,
				className:
					'bg-transparent text-primary ring-[1.5px] ring-inset ring-muted hover:bg-muted',
			},
			{
				variant: 'ghost-red',
				isOutline: true,
				className:
					'bg-transparent text-destructive ring-[1.5px] ring-inset ring-destructive hover:bg-destructive hover:text-white',
			},
		],
		defaultVariants: {
			variant: 'primary',
			size: 'default',
			isOutline: false,
		},
	},
)

export interface ButtonProps
	extends React.ButtonHTMLAttributes<HTMLButtonElement>,
		VariantProps<typeof buttonVariants> {
	isOutline?: boolean
	isLarge?: boolean
	isInput?: boolean
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
	(
		{
			className,
			variant,
			isOutline = false,
			isLarge = false,
			isInput = false,
			children,
			...props
		},
		ref,
	) => {
		const size = isLarge ? 'lg' : isInput ? 'input' : 'default'

		return (
			<button
				className={cn(buttonVariants({ variant, size, isOutline, className }))}
				ref={ref}
				data-testid="ShadcnButton"
				{...props}
			>
				<div className="inline-flex items-center justify-center min-h-[1.25rem] [&>svg]:text-xs [&>svg]:mr-2 [&>svg]:-ml-1">
					{children}
				</div>
			</button>
		)
	},
)
Button.displayName = 'Button'

export { Button, buttonVariants }
